"use client";

import {
  getAppLogDirectBaseUrl,
  getAppLogEnvironment,
  getAppLogErrorSampleRate,
  shouldPostAppLogsDirect,
  shouldSendAppLogs,
} from "@/lib/utils/app-log-env";
import type { AppLogPayload } from "@/types/app-log-types";
import { WEB_ENDPOINTS } from "../endpoints";

type AppLogInput = Omit<AppLogPayload, "environment"> & {
  environment?: AppLogPayload["environment"];
};

type AppLogOptions = {
  /**
   * Subject this log to session sampling and to the tighter noisy-source
   * budget. Set for the unbounded sources (window error / unhandledrejection).
   *
   * Leave unset for deliberate one-shot diagnostics — the 404 page event and
   * the global-error boundary. Those fire once, are already self-limiting, and
   * would be actively misleading if only a tenth of them arrived.
   */
  sampled?: boolean;
};

const PROXY_APP_LOGS_URL = "/api/app-logs";
const APP_NAME = "snappy-fix-web";
const BOT_USER_AGENT_PATTERN =
  /Chrome-Lighthouse|Lighthouse|PageSpeed|Google Page Speed|GTmetrix|Pingdom|bot|crawler|spider|Yandex|bingbot|Baiduspider/i;

/**
 * Two ceilings, both counted from when this module loads.
 *
 * These are the backstop against the ad-error path: AdSense creatives throw
 * routinely, every throw reaches our window listeners, and without a cap one
 * creative stuck in a loop can post indefinitely for the life of the tab.
 *
 * The noisy budget is deliberately lower than the overall one. A single shared
 * cap would let a burst of ad errors consume the entire allowance, so a
 * genuine global_error (level "fatal") arriving later in the session would be
 * silently dropped — losing the single most valuable log to the least valuable
 * ones. Reserving headroom means deliberate diagnostics always get through.
 *
 * Both survive client-side navigation (module lifetime, not route lifetime),
 * which is the point — a per-route counter would reset on every soft
 * navigation and cap nothing on a site people click through.
 */
const MAX_BROWSER_LOGS_PER_PAGE = 5;
const MAX_SAMPLED_LOGS_PER_PAGE = 3;
const ERROR_SAMPLE_STORAGE_KEY = "snappy-fix:app-log-sampled";

let browserLogsSent = 0;
let sampledLogsSent = 0;
let sampleDecision: boolean | null = null;

/**
 * Decide once per session whether this visitor reports errors, and remember it
 * in sessionStorage so the decision holds across soft navigations and reloads
 * within the tab. Storage can throw (Safari private mode, blocked cookies), so
 * every access is guarded; the fallback is a per-page-load decision, which is
 * still correctly weighted, just less stable.
 */
function isSessionSampledIn() {
  const rate = getAppLogErrorSampleRate();
  if (rate >= 1) return true;
  if (rate <= 0) return false;

  if (sampleDecision !== null) return sampleDecision;

  try {
    const stored = sessionStorage.getItem(ERROR_SAMPLE_STORAGE_KEY);
    if (stored === "1" || stored === "0") {
      sampleDecision = stored === "1";
      return sampleDecision;
    }

    sampleDecision = Math.random() < rate;
    sessionStorage.setItem(ERROR_SAMPLE_STORAGE_KEY, sampleDecision ? "1" : "0");
  } catch {
    sampleDecision = Math.random() < rate;
  }

  return sampleDecision;
}

/**
 * Where the beacon goes, and how the body is labelled.
 *
 * URL and Content-Type are resolved together, in one place, because they must
 * agree: direct mode can fall back to the proxy when the Go base URL is
 * missing, and a fallback that kept the cross-origin Content-Type would post
 * text/plain to our own JSON route. That happens to work today only because
 * readJsonBody() ignores Content-Type and just JSON.parses the text — a
 * coincidence, not a contract, and exactly the kind of thing that breaks
 * silently the day someone hardens the route.
 *
 * Same-origin (default) costs one uncached Vercel Edge Request plus a function
 * invocation per log. Posting straight to the Go API costs neither, because a
 * third-party origin never touches Vercel's network — that is the whole point
 * of direct mode.
 *
 * Cross-origin the Content-Type MUST be text/plain. `application/json` is not
 * CORS-safelisted, so it turns the POST into a preflighted request — and
 * `navigator.sendBeacon` cannot perform a preflight, so the beacon would simply
 * never be sent. text/plain keeps it a "simple" request the browser dispatches
 * without asking permission first.
 *
 * The server does not need to return CORS headers for this to work: for simple
 * requests the browser sends the request and only blocks JS from reading the
 * *response*, which fire-and-forget never does. What the server DOES need is to
 * parse the body as JSON despite the text/plain label — standard for Go
 * handlers using json.NewDecoder(r.Body), which ignores Content-Type, but worth
 * confirming against the real endpoint before enabling direct mode.
 */
function resolveAppLogTransport() {
  const directBaseUrl = getAppLogDirectBaseUrl();

  if (shouldPostAppLogsDirect() && directBaseUrl) {
    return {
      url: `${directBaseUrl.replace(/\/$/, "")}${WEB_ENDPOINTS.create_app_log}`,
      contentType: "text/plain;charset=UTF-8",
    };
  }

  return { url: PROXY_APP_LOGS_URL, contentType: "application/json" };
}

function withAppLogDefaults(payload: AppLogInput): AppLogPayload {
  return {
    app_name: APP_NAME,
    environment: getAppLogEnvironment(),
    ...payload,
  };
}

function shouldSkipBrowserLog() {
  return (
    !shouldSendAppLogs() || BOT_USER_AGENT_PATTERN.test(navigator.userAgent)
  );
}

function sendWithBeacon(payload: AppLogPayload) {
  if (!navigator.sendBeacon) return false;

  const { url, contentType } = resolveAppLogTransport();
  const blob = new Blob([JSON.stringify(payload)], { type: contentType });

  return navigator.sendBeacon(url, blob);
}

function sendWithKeepaliveFetch(payload: AppLogPayload) {
  const { url, contentType } = resolveAppLogTransport();

  void fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": contentType,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

function scheduleBrowserLog(send: () => void) {
  const browserWindow = window as Window &
    typeof globalThis & {
      requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions,
      ) => number;
    };

  if (browserWindow.requestIdleCallback) {
    browserWindow.requestIdleCallback(send, { timeout: 2000 });
    return;
  }

  browserWindow.setTimeout(send, 0);
}

export function getBrowserAppLogContext() {
  return {
    runtime: "browser" as const,
    url: window.location.href,
    path: `${window.location.pathname}${window.location.search}`,
    method: "GET",
    referrer: document.referrer || undefined,
    browser: {
      language: navigator.language,
      online: navigator.onLine,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    },
    device: {
      platform: navigator.platform,
      screen: `${window.screen.width}x${window.screen.height}`,
    },
  };
}

export function fireAndForgetAppLog(
  payload: AppLogInput,
  options: AppLogOptions = {},
) {
  if (shouldSkipBrowserLog()) return;

  if (options.sampled) {
    if (!isSessionSampledIn()) return;
    if (sampledLogsSent >= MAX_SAMPLED_LOGS_PER_PAGE) return;
  }

  // Counted before the send is scheduled, not inside the callback, so a burst
  // of synchronous throws can't queue more work than the caps allow while the
  // idle callbacks are still pending.
  if (browserLogsSent >= MAX_BROWSER_LOGS_PER_PAGE) return;

  browserLogsSent += 1;
  if (options.sampled) sampledLogsSent += 1;

  const logPayload = withAppLogDefaults(payload);

  scheduleBrowserLog(() => {
    if (!sendWithBeacon(logPayload)) {
      sendWithKeepaliveFetch(logPayload);
    }
  });
}
